import { useState, useCallback, useEffect } from "react"
import { router, usePage } from "@inertiajs/react"
import { AnimatePresence, motion } from "motion/react"
import { CalendarDays, Sparkles, Plus, SquarePen, X as CloseIcon } from "lucide-react"
import { PageHeader } from "@/components/core/page_header"
import Layout from "@/pages/layouts/application"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { DateTimePicker } from "@/components/ui/datepicker"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import postsApi from "@/api/Organizations/PostsApi"
import contentApi from "@/api/Organizations/ContentApi"
import * as api from "@/api"
import XIcon from "@/assets/icons/x.svg?react"
import LinkedinIcon from "@/assets/icons/linkedin.svg?react"
import TwitterThreadEditor from "@/pages/organizations/posts/components/twitter_thread_editor"
import LinkedinThreadEditor from "@/pages/organizations/posts/components/linkedin_thread_editor"

const cx = (...classes) => classes.filter(Boolean).join(" ")
const POST_DELIMITER = "============="

const splitThread = (content = "") => {
  const parts = content
    .split(POST_DELIMITER)
    .map((p) => p.trim())
    .filter(Boolean)

  return parts.length > 0 ? parts : [""]
}

function ConnectAccountsPrompt() {
  const { organization } = usePage().props.auth

  const platforms = [
    { platform: "linkedin", logo: "/linkedin.png", name: "LinkedIn", description: "Publish posts to your LinkedIn profile, and add first comments." },
    { platform: "twitter", logo: "/twitter.png", name: "Twitter", description: "Cross-post your content to X." },
  ]

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground text-center">
        Connect a LinkedIn or X account to publish your posts.
      </p>
      <div className="grid grid-cols-2 gap-3">
        {platforms.map(({ platform, logo, name, description }) => (
          <div key={platform} className="flex flex-col gap-3 p-4 border rounded-md">
            <div className="flex items-center gap-1.5">
              <img src={logo} alt={name} className="size-5 shrink-0" />
              <span className="font-medium text-sm">{name}</span>
            </div>
            <p className="text-xs text-muted-foreground">{description}</p>
            <div className="mt-auto">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => {
                  const returnTo = window.location.pathname
                  window.location.href = `/auth/${organization.nano_id}/late/connect/${platform}?return_to=${encodeURIComponent(returnTo)}`
                }}
              >
                Connect
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function ListViewScreen({ posts: initialPosts, mode = "list", expectedPostsCount }) {
  const { organization, user } = usePage().props.auth
  const { integrations, social_accounts } = usePage().props

  const isOnboarding = mode === "onboarding"

  const linkedinProfile = integrations?.linkedin
  const twitterProfile = integrations?.twitter
  const twitterEnabled = !!twitterProfile?.connected
  const linkedinEnabled = !!linkedinProfile?.connected
  const bothAccountsDisconnected = !twitterEnabled && !linkedinEnabled
  // Drafts stay reviewable/editable without a connected account; publishing and
  // scheduling instead prompt the user to connect. This applies in every
  // context, not just onboarding.
  const needsConnect = bothAccountsDisconnected

  const twitterDisplayName = twitterProfile?.display_name || twitterProfile?.handle || organization?.name || "Your company"
  const linkedinDisplayName = social_accounts?.linkedin?.display_name || linkedinProfile?.display_name || linkedinProfile?.handle || organization?.name || "Your company"
  const twitterHandleLabel = twitterProfile?.handle || (user?.email ? user.email.split("@")[0] : "founder")
  const linkedinHandleLabel = linkedinProfile?.handle || (user?.email ? user.email.split("@")[0] : "founder")
  const twitterAvatarUrl = twitterProfile?.avatar_url
  const linkedinAvatarUrl = linkedinProfile?.avatar_url
  const twitterVerified = twitterProfile?.verified || false

  const createDraftState = useCallback((post) => {
    const bothDisconnected = !twitterEnabled && !linkedinEnabled
    const forceSelect = bothDisconnected

    const postTargets = post.targets?.length
      ? {
          twitter: post.targets.includes("twitter"),
          linkedin: post.targets.includes("linkedin"),
        }
      : {
          twitter: forceSelect ? true : twitterEnabled,
          linkedin: forceSelect ? true : linkedinEnabled,
        }

    const selectionOrder = []
    if (postTargets.twitter) selectionOrder.push("twitter")
    if (postTargets.linkedin) selectionOrder.push("linkedin")
    if (!selectionOrder.includes("twitter") && twitterEnabled) selectionOrder.push("twitter")
    if (!selectionOrder.includes("linkedin") && linkedinEnabled) selectionOrder.push("linkedin")

    return {
      targets: postTargets,
      selectionOrder,
      activePreview: forceSelect ? "linkedin" : postTargets.twitter ? "twitter" : postTargets.linkedin ? "linkedin" : null,
      crossPostEnabled: postTargets.twitter && postTargets.linkedin,
      showSchedule: false,
      scheduleDate: post.status === "draft" ? null : (post.scheduled_at ? new Date(post.scheduled_at) : null),
      twitterPosts: splitThread(post.twitter_content || post.content || ""),
      linkedinPosts: [post.linkedin_content || post.content || ""],
      replyContent: post.reply?.content || "",
    }
  }, [twitterEnabled, linkedinEnabled, isOnboarding])

  const [dismissedIds, setDismissedIds] = useState([])
  const [processingId, setProcessingId] = useState(null)
  const [showPublishDialogForId, setShowPublishDialogForId] = useState(null)
  const [showUnscheduleDialogForId, setShowUnscheduleDialogForId] = useState(null)
  const [showConnectModal, setShowConnectModal] = useState(false)
  const [savingById, setSavingById] = useState({})
  const [draftStateById, setDraftStateById] = useState(() => {
    const entries = (initialPosts || []).map((post) => [post.id, createDraftState(post)])
    return Object.fromEntries(entries)
  })

  const reviewPosts = (initialPosts || []).filter(
    (p) => ["draft", "approved", "published"].includes(p.status) && !dismissedIds.includes(p.id)
  )

  useEffect(() => {
    setDraftStateById((prev) => {
      const next = { ...prev }
      let changed = false

      for (const post of initialPosts || []) {
        if (!next[post.id]) {
          next[post.id] = createDraftState(post)
          changed = true
        }
      }

      return changed ? next : prev
    })
  }, [initialPosts, createDraftState])

  useEffect(() => {
    if (!isOnboarding || !expectedPostsCount) return
    const pending = expectedPostsCount - (initialPosts || []).length
    if (pending <= 0) return

    const interval = setInterval(() => {
      router.reload({ only: ["posts"], preserveScroll: true })
    }, 3000)

    return () => clearInterval(interval)
  }, [isOnboarding, expectedPostsCount, initialPosts])

  const dismissPost = useCallback((id) => {
    setDismissedIds((prev) => [...prev, id])
    setProcessingId(null)
    setShowPublishDialogForId(null)
    setShowUnscheduleDialogForId(null)
  }, [])

  const updateDraftState = useCallback((post, updater) => {
    setDraftStateById((prev) => {
      const current = prev[post.id] || createDraftState(post)
      const nextValue = typeof updater === "function" ? updater(current) : { ...current, ...updater }
      return { ...prev, [post.id]: nextValue }
    })
  }, [createDraftState])

  const getDraft = useCallback((post) => draftStateById[post.id] || createDraftState(post), [draftStateById, createDraftState])

  const getSelectedTargets = useCallback((draft) => (
    Object.entries(draft.targets)
      .filter(([, value]) => value)
      .map(([key]) => key)
  ), [])

  const getTwitterJoinedContent = useCallback((draft) => draft.twitterPosts.join(`\n\n${POST_DELIMITER}\n\n`), [])
  const getLinkedinJoinedContent = useCallback((draft) => draft.linkedinPosts.join("\n\n"), [])

  const buildPostPayload = useCallback((draft, options = {}) => {
    const payload = {
      post: {
        twitter_content: getTwitterJoinedContent(draft),
        linkedin_content: getLinkedinJoinedContent(draft),
        content: getLinkedinJoinedContent(draft),
      },
    }

    if (options.scheduleDate) {
      payload.post.status = "approved"
      payload.post.scheduled_at = options.scheduleDate.toISOString()
    }

    return payload
  }, [getTwitterJoinedContent, getLinkedinJoinedContent])

  const handleSaveStart = useCallback((postId) => {
    setSavingById((prev) => ({ ...prev, [postId]: true }))
  }, [])

  const handleSaveEnd = useCallback((postId) => {
    setSavingById((prev) => ({ ...prev, [postId]: false }))
  }, [])

  const handlePublishNow = useCallback(
    (post, draft) => {
      setProcessingId(post.id)
      const selectedTargets = getSelectedTargets(draft)

      router.patch(
        postsApi.update.path({ org_nano_id: organization.nano_id, id: post.id }),
        buildPostPayload(draft),
        {
          preserveScroll: true,
          onSuccess: () => {
            const data = selectedTargets.length > 0 ? { targets: selectedTargets } : {}
            router.post(
              api.organizationsPostsPublish.create.path({
                org_nano_id: organization.nano_id,
                post_id: post.id,
              }),
              data,
              {
                preserveScroll: true,
                onSuccess: () => dismissPost(post.id),
                onError: () => setProcessingId(null),
              }
            )
          },
          onError: () => setProcessingId(null),
        }
      )
    },
    [organization.nano_id, dismissPost, buildPostPayload, getSelectedTargets]
  )

  const handleSchedule = useCallback(
    (post, draft) => {
      if (!draft.scheduleDate) return

      setProcessingId(post.id)
      const payload = buildPostPayload(draft, { scheduleDate: draft.scheduleDate })
      router.patch(
        postsApi.update.path({ org_nano_id: organization.nano_id, id: post.id }),
        payload,
        {
          preserveScroll: true,
          onSuccess: () => dismissPost(post.id),
          onError: () => setProcessingId(null),
        }
      )
    },
    [organization.nano_id, dismissPost, buildPostPayload]
  )

  const handleUnschedule = useCallback(
    (post) => {
      setProcessingId(post.id)
      router.patch(
        postsApi.update.path({ org_nano_id: organization.nano_id, id: post.id }),
        { post: { status: "draft" } },
        {
          preserveScroll: true,
          onSuccess: () => dismissPost(post.id),
          onError: () => setProcessingId(null),
        }
      )
    },
    [organization.nano_id, dismissPost]
  )

  const selectPreview = useCallback((post, key, enabled) => {
    if (!enabled) return

    updateDraftState(post, (current) => {
      const nextTargets = current.targets[key]
        ? current.targets
        : { ...current.targets, [key]: true }
      const nextSelectionOrder = current.selectionOrder.includes(key)
        ? current.selectionOrder
        : [...current.selectionOrder, key]

      return {
        ...current,
        targets: nextTargets,
        selectionOrder: nextSelectionOrder,
        activePreview: key,
        crossPostEnabled: nextTargets.twitter && nextTargets.linkedin,
      }
    })
  }, [updateDraftState])

  const toggleTarget = useCallback((post, key, enabled) => {
    if (!enabled) return

    updateDraftState(post, (current) => {
      const wasSelected = current.targets[key]
      const nextTargets = { ...current.targets, [key]: !wasSelected }
      const nextSelectionOrder = wasSelected
        ? current.selectionOrder.filter((platform) => platform !== key)
        : current.selectionOrder.includes(key)
          ? current.selectionOrder
          : [...current.selectionOrder, key]

      const nextActivePreview = wasSelected && current.activePreview === key
        ? (nextSelectionOrder.find((platform) => nextTargets[platform]) || null)
        : (wasSelected ? current.activePreview : key)

      return {
        ...current,
        targets: nextTargets,
        selectionOrder: nextSelectionOrder,
        activePreview: nextActivePreview,
        crossPostEnabled: nextTargets.twitter && nextTargets.linkedin,
      }
    })
  }, [updateDraftState])

  const updateTwitterPost = useCallback((post, index, content) => {
    updateDraftState(post, (current) => {
      const nextTwitter = [...current.twitterPosts]
      nextTwitter[index] = content

      if (!current.crossPostEnabled) {
        return { ...current, twitterPosts: nextTwitter }
      }

      const nextLinkedin = [...current.linkedinPosts]
      nextLinkedin[index] = content

      return {
        ...current,
        twitterPosts: nextTwitter,
        linkedinPosts: nextLinkedin,
      }
    })
  }, [updateDraftState])

  const updateLinkedinPost = useCallback((post, index, content) => {
    updateDraftState(post, (current) => {
      const nextLinkedin = [...current.linkedinPosts]
      nextLinkedin[index] = content

      if (!current.crossPostEnabled) {
        return { ...current, linkedinPosts: nextLinkedin }
      }

      const nextTwitter = [...current.twitterPosts]
      nextTwitter[index] = content

      return {
        ...current,
        linkedinPosts: nextLinkedin,
        twitterPosts: nextTwitter,
      }
    })
  }, [updateDraftState])

  const addTwitterPost = useCallback((post) => {
    updateDraftState(post, (current) => {
      const nextTwitter = [...current.twitterPosts, ""]
      if (!current.crossPostEnabled) return { ...current, twitterPosts: nextTwitter }
      return {
        ...current,
        twitterPosts: nextTwitter,
        linkedinPosts: [...current.linkedinPosts, ""],
      }
    })
  }, [updateDraftState])

  const addLinkedinPost = useCallback((post) => {
    updateDraftState(post, (current) => {
      const nextLinkedin = [...current.linkedinPosts, ""]
      if (!current.crossPostEnabled) return { ...current, linkedinPosts: nextLinkedin }
      return {
        ...current,
        linkedinPosts: nextLinkedin,
        twitterPosts: [...current.twitterPosts, ""],
      }
    })
  }, [updateDraftState])

  const removeTwitterPost = useCallback((post, index) => {
    updateDraftState(post, (current) => {
      if (current.twitterPosts.length <= 1) return current
      const nextTwitter = current.twitterPosts.filter((_, i) => i !== index)
      if (!current.crossPostEnabled) return { ...current, twitterPosts: nextTwitter }
      const nextLinkedin = current.linkedinPosts.length > 1
        ? current.linkedinPosts.filter((_, i) => i !== index)
        : current.linkedinPosts
      return {
        ...current,
        twitterPosts: nextTwitter,
        linkedinPosts: nextLinkedin,
      }
    })
  }, [updateDraftState])

  const removeLinkedinPost = useCallback((post, index) => {
    updateDraftState(post, (current) => {
      if (current.linkedinPosts.length <= 1) return current
      const nextLinkedin = current.linkedinPosts.filter((_, i) => i !== index)
      if (!current.crossPostEnabled) return { ...current, linkedinPosts: nextLinkedin }
      const nextTwitter = current.twitterPosts.length > 1
        ? current.twitterPosts.filter((_, i) => i !== index)
        : current.twitterPosts
      return {
        ...current,
        linkedinPosts: nextLinkedin,
        twitterPosts: nextTwitter,
      }
    })
  }, [updateDraftState])

  const updateScheduleDate = useCallback((post, date) => {
    updateDraftState(post, (current) => ({ ...current, scheduleDate: date }))
  }, [updateDraftState])

  return (
    <Layout>
      <div className="space-y-8 pb-24">
        {!isOnboarding && (
          <PageHeader title="List View">
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => router.get(contentApi.show.path({ org_nano_id: organization.nano_id }))}
              >
                Calendar View
              </Button>
              <Button
                size="sm"
                variant="default"
                onClick={() => router.get(postsApi.new.path({ org_nano_id: organization.nano_id }))}
              >
                New Post
              </Button>
            </div>
          </PageHeader>
        )}

        {isOnboarding && (
          <div className="mx-auto w-full max-w-[960px]">
            <Card>
              <CardHeader>
                <CardTitle>Hey there 👋 thanks for signing up!</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-foreground">We drafted your first few posts below. Review them, connect your accounts, and publish when you're ready. </p>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="mx-auto w-full max-w-[960px] space-y-6">
          {bothAccountsDisconnected && (
            <div className="rounded-2xl border bg-card p-5">
              <ConnectAccountsPrompt />
            </div>
          )}
          <>

          <AnimatePresence mode="popLayout">
            {reviewPosts.map((post) => (
              (() => {
                const draft = getDraft(post)
                const isPublished = post.status === "published" || !!post.published_at
                const isScheduled = !isPublished && post.status === "approved"
                const selectedTargets = getSelectedTargets(draft)
                const hasSelectedTargets = needsConnect || selectedTargets.length > 0
                const selectedPlatformsInOrder = draft.selectionOrder.filter((platform) => draft.targets[platform])
                const selectedNotTracked = ["twitter", "linkedin"].filter(
                  (platform) => draft.targets[platform] && !selectedPlatformsInOrder.includes(platform)
                )
                const unselectedPlatforms = ["twitter", "linkedin"].filter((platform) => !draft.targets[platform])
                const pillOrder = [...selectedPlatformsInOrder, ...selectedNotTracked, ...unselectedPlatforms]
                const twitterJoinedContent = getTwitterJoinedContent(draft)
                const linkedinJoinedContent = getLinkedinJoinedContent(draft)
                const hasAnyContent = draft.twitterPosts.some((p) => p.trim()) || draft.linkedinPosts.some((p) => p.trim())
                const actionDisabled = processingId === post.id || !hasSelectedTargets || !hasAnyContent
                const statusLabel = isPublished
                  ? "Published"
                  : isScheduled
                    ? "Scheduled"
                    : "Draft"
                const statusClassName = isPublished || isScheduled
                  ? "bg-green-100 text-green-700"
                  : "bg-secondary text-secondary-foreground"

                return (
                  <motion.div
                    key={post.id}
                    layout
                    initial={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.97, filter: "blur(4px)" }}
                    transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                    className="rounded-2xl bg-card p-4 shadow-sm space-y-2"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-2">
                        {!bothAccountsDisconnected && (
                          <div className="flex items-center gap-1 rounded-md bg-background px-2 py-1 shadow-sm">
                            {pillOrder.map((platform) => {
                              const isTwitter = platform === "twitter"
                              const enabled = isTwitter ? twitterEnabled : linkedinEnabled
                              const selected = draft.targets[platform]
                              const active = draft.activePreview === platform
                              const displayName = isTwitter ? twitterDisplayName : linkedinDisplayName
                              const selectedIconSize = isTwitter ? "h-3 w-3" : "h-3.5 w-3.5"
                              const unselectedIconSize = "h-3.5 w-3.5"

                              return (
                                <div
                                  key={`${post.id}-${platform}`}
                                  className={cx(
                                    "group flex items-center rounded-lg px-0.5 transition-colors duration-200",
                                    selected && active
                                      ? "bg-foreground text-background hover:bg-foreground/90"
                                      : selected
                                        ? "text-foreground hover:bg-muted/60"
                                        : "bg-transparent text-muted-foreground hover:bg-muted/40",
                                    !enabled && "cursor-not-allowed opacity-50"
                                  )}
                                >
                                  <button
                                    type="button"
                                    onClick={() => selectPreview(post, platform, enabled)}
                                    disabled={!enabled}
                                    className={cx(
                                      "flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition",
                                      selected && !active && "text-foreground"
                                    )}
                                  >
                                    {selected ? (
                                      isTwitter ? (
                                        <XIcon className={`${selectedIconSize} fill-current`} />
                                      ) : (
                                        <LinkedinIcon className={`${selectedIconSize} fill-current`} />
                                      )
                                    ) : (
                                      <span className={`relative block ${unselectedIconSize}`}>
                                        <Plus className={`${unselectedIconSize} absolute inset-0 opacity-100 transition-opacity duration-200 ease-in-out group-hover:opacity-0`} />
                                        {isTwitter ? (
                                          <XIcon className={`${unselectedIconSize} absolute inset-0 fill-current opacity-0 transition-opacity duration-200 ease-in-out group-hover:opacity-100`} />
                                        ) : (
                                          <LinkedinIcon className={`${unselectedIconSize} absolute inset-0 fill-current opacity-0 transition-opacity duration-200 ease-in-out group-hover:opacity-100`} />
                                        )}
                                      </span>
                                    )}

                                    {selected ? (
                                      <span className="max-w-32 truncate text-[11px] font-medium">{displayName}</span>
                                    ) : (
                                      <span className="max-w-0 overflow-hidden whitespace-nowrap text-[11px] opacity-0 transition-all duration-200 group-hover:max-w-32 group-hover:opacity-100">
                                        {displayName}
                                      </span>
                                    )}
                                  </button>

                                  {selected && (
                                    <button
                                      type="button"
                                      onClick={() => toggleTarget(post, platform, enabled)}
                                      disabled={!enabled}
                                      className="rounded-full p-0.5 transition hover:bg-background/20"
                                      aria-label={isTwitter ? "Remove X target" : "Remove LinkedIn target"}
                                    >
                                      <CloseIcon className="h-2.5 w-2.5" />
                                    </button>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )}

                        <Badge className={statusClassName}>
                          {statusLabel}
                        </Badge>
                      </div>

                      {hasSelectedTargets && !isPublished && (
                        <div className="flex items-center gap-2">
                          {isScheduled ? (
                            <>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="rounded-sm text-muted-foreground hover:text-foreground"
                                onClick={() => needsConnect ? setShowConnectModal(true) : setShowUnscheduleDialogForId(post.id)}
                                disabled={processingId === post.id}
                              >
                                Unschedule
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                className="rounded-sm"
                                onClick={() => needsConnect ? setShowConnectModal(true) : setShowPublishDialogForId(post.id)}
                                disabled={needsConnect ? false : actionDisabled}
                              >
                                Publish
                              </Button>

                              <Dialog
                                open={showUnscheduleDialogForId === post.id}
                                onOpenChange={(open) => setShowUnscheduleDialogForId(open ? post.id : null)}
                              >
                                <DialogContent className="sm:max-w-md">
                                  <DialogHeader>
                                    <DialogTitle>Unschedule Post</DialogTitle>
                                    <DialogDescription>
                                      Are you sure you want to unschedule this post?
                                    </DialogDescription>
                                  </DialogHeader>
                                  <DialogFooter className="flex gap-2 sm:gap-0">
                                    <Button
                                      variant="outline"
                                      onClick={() => setShowUnscheduleDialogForId(null)}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      variant="destructive"
                                      onClick={() => {
                                        setShowUnscheduleDialogForId(null)
                                        handleUnschedule(post)
                                      }}
                                    >
                                      Unschedule
                                    </Button>
                                  </DialogFooter>
                                </DialogContent>
                              </Dialog>

                            </>
                          ) : draft.showSchedule ? (
                            <>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => updateDraftState(post, (current) => ({ ...current, showSchedule: false }))}
                                className="rounded-sm text-muted-foreground hover:text-foreground"
                              >
                                Cancel
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => needsConnect ? setShowConnectModal(true) : handleSchedule(post, draft)}
                                disabled={needsConnect ? false : (actionDisabled || !draft.scheduleDate)}
                                className="rounded-sm bg-amber-500 hover:bg-amber-600"
                              >
                                Confirm Schedule
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => needsConnect ? setShowConnectModal(true) : updateDraftState(post, (current) => ({ ...current, showSchedule: true }))}
                                className="rounded-sm text-muted-foreground hover:text-foreground"
                                disabled={processingId === post.id}
                              >
                                Schedule
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                className="rounded-sm"
                                onClick={() => needsConnect ? setShowConnectModal(true) : setShowPublishDialogForId(post.id)}
                                disabled={needsConnect ? false : actionDisabled}
                              >
                                Publish
                              </Button>
                              <Dialog
                                open={showPublishDialogForId === post.id}
                                onOpenChange={(open) => setShowPublishDialogForId(open ? post.id : null)}
                              >
                                <DialogContent className="sm:max-w-md">
                                  <DialogHeader>
                                    <DialogTitle>Publish Now</DialogTitle>
                                    <DialogDescription>
                                      Are you sure you want to publish this post instantly?
                                    </DialogDescription>
                                  </DialogHeader>
                                  <DialogFooter className="flex gap-2 sm:gap-0">
                                    <Button
                                      variant="outline"
                                      onClick={() => setShowPublishDialogForId(null)}
                                    >
                                      Reconsider
                                    </Button>
                                    <Button
                                      onClick={() => {
                                        setShowPublishDialogForId(null)
                                        handlePublishNow(post, draft)
                                      }}
                                    >
                                      Publish
                                    </Button>
                                  </DialogFooter>
                                </DialogContent>
                              </Dialog>
                            </>
                          )}

                          {savingById[post.id] && (
                            <span className="text-xs text-muted-foreground">Saving...</span>
                          )}
                        </div>
                      )}
                    </div>

                    <div
                      className={cx(
                        "grid transition-all duration-200",
                        draft.showSchedule ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                      )}
                    >
                      <div className="overflow-hidden">
                        <div className="rounded-xl border-0 bg-background pb-2">
                          <DateTimePicker value={draft.scheduleDate} onChange={(date) => updateScheduleDate(post, date)} />
                        </div>
                      </div>
                    </div>

                    <div className="mt-0 w-full">
                        {draft.activePreview === "twitter" && draft.targets.twitter && (
                          <Card className="rounded-2xl shadow-sm">
                            <CardContent className="p-5">
                              <TwitterThreadEditor
                                posts={draft.twitterPosts}
                                onUpdatePost={(index, content) => updateTwitterPost(post, index, content)}
                                onAddPost={() => addTwitterPost(post)}
                                onRemovePost={(index) => removeTwitterPost(post, index)}
                                joinedContent={twitterJoinedContent}
                                initialContent={post.twitter_content || post.content || ""}
                                saveUrl={postsApi.update.path({ org_nano_id: organization.nano_id, id: post.id })}
                                onSaveStart={() => handleSaveStart(post.id)}
                                onSaveEnd={() => handleSaveEnd(post.id)}
                                displayName={twitterDisplayName}
                                handleLabel={twitterHandleLabel}
                                avatarUrl={twitterAvatarUrl}
                                verified={twitterVerified}
                                disabled={processingId === post.id || isPublished}
                              />
                            </CardContent>
                          </Card>
                        )}

                        {draft.activePreview === "linkedin" && draft.targets.linkedin && (
                          <Card className="rounded-2xl shadow-sm">
                            <CardContent className="p-5">
                              <LinkedinThreadEditor
                                posts={draft.linkedinPosts}
                                onUpdatePost={(index, content) => updateLinkedinPost(post, index, content)}
                                onAddPost={() => addLinkedinPost(post)}
                                onRemovePost={(index) => removeLinkedinPost(post, index)}
                                joinedContent={linkedinJoinedContent}
                                initialContent={post.linkedin_content || post.content || ""}
                                initialReplyContent={post.reply?.content || ""}
                                replyId={post.reply?.id || null}
                                saveUrl={postsApi.update.path({ org_nano_id: organization.nano_id, id: post.id })}
                                onSaveStart={() => handleSaveStart(post.id)}
                                onSaveEnd={() => handleSaveEnd(post.id)}
                                displayName={linkedinDisplayName}
                                handleLabel={linkedinHandleLabel}
                                avatarUrl={linkedinAvatarUrl}
                                organizationId={organization?.id}
                                onReplyChange={(content) => updateDraftState(post, (current) => ({ ...current, replyContent: content }))}
                                disabled={processingId === post.id || isPublished}
                              />
                            </CardContent>
                          </Card>
                        )}
                      </div>
                  </motion.div>
                )
              })()
            ))}
          </AnimatePresence>

          {isOnboarding && expectedPostsCount && reviewPosts.length < expectedPostsCount && (
            Array.from({ length: expectedPostsCount - reviewPosts.length }).map((_, i) => (
              <motion.div
                key={`skeleton-${i}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="rounded-2xl bg-card p-4 shadow-sm"
              >
                <div className="animate-pulse space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-24 rounded bg-muted" />
                    <div className="h-4 w-14 rounded bg-muted" />
                  </div>
                  <div className="space-y-2 pt-1">
                    <div className="h-3.5 w-full rounded bg-muted" />
                    <div className="h-3.5 w-5/6 rounded bg-muted" />
                    <div className="h-3.5 w-4/6 rounded bg-muted" />
                    <div className="h-3.5 w-3/6 rounded bg-muted" />
                  </div>
                  <p className="text-xs text-muted-foreground pt-1">Still drafting your next post...</p>
                </div>
              </motion.div>
            ))
          )}

          {reviewPosts.length === 0 && (
            (() => {
              const hasNoPosts = (initialPosts?.length || 0) === 0
              const showCreateCta = !isOnboarding && hasNoPosts

              return (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                  className="flex flex-col items-center justify-center py-20 text-center"
                >
                  <h2 className="text-xl font-semibold mb-2">
                    {showCreateCta
                      ? "No posts yet"
                      : isOnboarding ? "Great start — you’re all set!" : "You're all caught up!"}
                  </h2>
                  <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                    {showCreateCta
                      ? "Drafts, scheduled, and published posts will show up here. Create your first one to get started."
                      : isOnboarding
                        ? "Your starter drafts are done. Head to the content calendar to continue planning."
                        : "All your draft posts have been reviewed. Head to the content calendar to see everything."}
                  </p>
                  {showCreateCta ? (
                    <Button
                      onClick={() =>
                        router.get(postsApi.new.path({ org_nano_id: organization.nano_id }))
                      }
                    >
                      <SquarePen className="w-4 h-4 mr-2" />
                      Create your first post
                    </Button>
                  ) : (
                    <Button
                      onClick={() =>
                        router.get(
                          contentApi.show.path({
                            org_nano_id: organization.nano_id,
                          })
                        )
                      }
                    >
                      <CalendarDays className="w-4 h-4 mr-2" />
                      Go to Calendar
                    </Button>
                  )}
                </motion.div>
              )
            })()
          )}
            </>
        </div>
      </div>

      <Dialog open={showConnectModal} onOpenChange={setShowConnectModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Connect an account to publish</DialogTitle>
            <DialogDescription>
              Link your LinkedIn or X account to start publishing posts.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            {[
              { platform: "linkedin", logo: "/linkedin.png", name: "LinkedIn", description: "Publish posts to your LinkedIn profile, and add first comments." },
              { platform: "twitter", logo: "/twitter.png", name: "Twitter", description: "Cross-post your content to X." },
            ].map(({ platform, logo, name, description }) => (
              <div key={platform} className="flex flex-col gap-3 p-4 border rounded-md">
                <div className="flex items-center gap-1.5">
                  <img src={logo} alt={name} className="size-5 shrink-0" />
                  <span className="font-medium text-sm">{name}</span>
                </div>
                <p className="text-xs text-muted-foreground">{description}</p>
                <div className="mt-auto">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setShowConnectModal(false)
                      const returnTo = isOnboarding ? `/${organization.nano_id}/setup/power_through` : window.location.pathname
                      window.location.href = `/auth/${organization.nano_id}/late/connect/${platform}?return_to=${encodeURIComponent(returnTo)}`
                    }}
                  >
                    Connect
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  )
}
